import React from 'react';
import { CheckCircle, AlertCircle, Info, FileCheck, Shield } from 'lucide-react';
interface ReadinessData {
  percentage: number;
  completed: number;
  flagged: number;
  skipped: number;
  conflicting: number;
  isReady: boolean;
  needsReview: boolean;
}
interface ReadinessWidgetProps {
  readiness: ReadinessData;
  milestoneStatuses: Record<string, string>;
  formData: any;
  isSidebar?: boolean;
  onFinalize?: () => void;
  onRequestReview?: () => void;
}
export function ReadinessWidget({
  readiness,
  milestoneStatuses,
  formData,
  isSidebar = false,
  onFinalize,
  onRequestReview
}: ReadinessWidgetProps) {
  return <div className={`bg-white rounded-xl border border-borderColor p-4 sm:p-6 shadow-sm ${isSidebar ? 'sticky top-6' : 'mb-6'}`} data-id="element-1067">
      <h3 className="text-base sm:text-lg font-semibold text-primary mb-4" data-id="element-1068">
        Will Readiness
      </h3>

      <div className="flex items-center gap-4 mb-6" data-id="element-1069">
        <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center" data-id="element-1070">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36" data-id="element-1071">
            <path className="text-borderColor" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" data-id="element-1072" />
            <path className={`${readiness.isReady ? 'text-turquoise' : readiness.needsReview ? 'text-amber-500' : 'text-violetBlue'} transition-all duration-1000`} strokeDasharray={`${readiness.percentage}, 100`} strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" data-id="element-1073" />
          </svg>
          <span className="absolute text-sm font-bold text-primary" data-id="element-1074">
            {readiness.percentage}%
          </span>
        </div>
        <div data-id="element-1075">
          <p className="font-medium text-primary text-sm sm:text-base" data-id="element-1076">
            {readiness.isReady ? 'Ready to finalize' : readiness.needsReview ? 'Action required' : 'Draft in progress'}
          </p>
          <p className="text-xs sm:text-sm text-secondary" data-id="element-1077">
            {readiness.isReady ? 'All sections complete' : 'Draft in progress'}
          </p>
        </div>
      </div>

      <div className="space-y-3 mb-6" data-id="element-1078">
        {milestoneStatuses.about === 'completed' && <div className="flex items-start gap-2 text-xs sm:text-sm" data-id="element-1079">
            <CheckCircle className="w-4 h-4 text-turquoise mt-0.5 flex-shrink-0" data-id="element-1080" />
            <span className="text-primary" data-id="element-1081">Personal info complete</span>
          </div>}
        {milestoneStatuses.family === 'completed' && <div className="flex items-start gap-2 text-xs sm:text-sm" data-id="element-1082">
            <CheckCircle className="w-4 h-4 text-turquoise mt-0.5 flex-shrink-0" data-id="element-1083" />
            <span className="text-primary" data-id="element-1084">Family details confirmed</span>
          </div>}
        {milestoneStatuses.executor === 'flagged' && <div className="flex items-start gap-2 text-xs sm:text-sm" data-id="element-1085">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-1086" />
            <span className="text-primary font-medium" data-id="element-1087">
              Executor needs decision
            </span>
          </div>}
        {milestoneStatuses.personalGifts === 'skipped' && <div className="flex items-start gap-2 text-xs sm:text-sm" data-id="element-1088">
            <div className="w-4 h-4 rounded-full border-2 border-disabled mt-0.5 flex-shrink-0" data-id="element-1089" />
            <span className="text-secondary" data-id="element-1090">Personal Gifts (Skipped)</span>
          </div>}
        {milestoneStatuses.financialGifts === 'flagged' && readiness.conflicting > 0 && <div className="flex items-start gap-2 text-xs sm:text-sm" data-id="element-1091">
              <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" data-id="element-1092" />
              <span className="text-destructive font-medium" data-id="element-1093">
                Distribution must total 100%
              </span>
            </div>}
        {formData.specialFuneralInstructions === '' && milestoneStatuses.funeral === 'completed' && <div className="flex items-start gap-2 text-xs sm:text-sm" data-id="element-1094">
              <Info className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" data-id="element-1095" />
              <span className="text-secondary" data-id="element-1096">
                Special funeral instructions (Skipped)
              </span>
            </div>}
      </div>

      {isSidebar && onFinalize && onRequestReview && <div className="space-y-3 pt-4 border-t border-borderColor" data-id="element-1097">
          <button onClick={onRequestReview} className="w-full bg-white border border-borderColor text-primary py-2.5 sm:py-3 px-4 rounded-xl font-medium hover:bg-aliceBlue transition-colors flex items-center justify-center gap-2 text-sm sm:text-base" data-id="element-1098">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1099" />
            Request Lawyer Review
          </button>
          <button disabled={!readiness.isReady} onClick={onFinalize} className="w-full bg-violetBlue text-white py-2.5 sm:py-3 px-4 rounded-xl font-medium hover:bg-violetBlue/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base" data-id="element-1100">
            <FileCheck className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1101" />
            Finalize & Generate Will
          </button>
        </div>}
    </div>;
}